import React, { useState } from 'react';
import './Board.scss';

// Helper to get constants safely
const getConstant = (gameData, key, defaultValue) => {
  return gameData?.constants?.[key] ?? defaultValue;
};

const Board = ({ onClose, gameData }) => {
  const [activeSection, setActiveSection] = useState('gameInfo');

  // Get constants
  const dailyTokenPool = getConstant(gameData, 'DAILY_TOKEN_POOL', 2000);
  const maxHourlyReward = getConstant(gameData, 'MAX_HOURLY_REWARD', 50);

  const sections = {
    gameInfo: {
      title: "What is Reddicks Game?",
      content: (
        <>
          <p>Step into a fully-realized 3D cyberpunk world where your entrepreneurial dreams come to life! Reddicks Game leverages cutting-edge WebGL technology for a stunning browser-based 3D experience without downloads.</p>
          <p>Begin your journey by purchasing mining rigs in your virtual cyberpunk office, generating passive $REDDICKS income while you're away. Return to collect your earnings and expand your digital empire.</p>
          <p>This isn't just a typical blockchain game—it's an immersive tycoon experience bridging traditional gaming and Web3 play-to-earn mechanics, all connected to the revolutionary Radix network.</p>
          <p>The game is evolving, with plans for multiplayer features, NFT integration, and a complete virtual economy.</p>
        </>
      )
    },
    controls: {
      title: "Controls",
      content: (
        <>
          <p>Navigate the office using your keyboard and mouse:</p>
          <ul>
            <li><strong>W/A/S/D (or Z/Q/S/D):</strong> Move Forward/Left/Backward/Right</li>
            <li><strong>Shift:</strong> Hold to Run</li>
            <li><strong>Mouse Movement:</strong> Look Around (requires cursor lock)</li>
            <li><strong>Click on Game Window:</strong> Lock Cursor (enables looking around)</li>
            <li><strong>Middle Mouse Button:</strong> Toggle Cursor Lock (allows interaction with menus/UI)</li>
            <li><strong>Click on Buttons (e.g., 'Board', 'Mining Rigs'):</strong> Open Menus (automatically unlocks cursor)</li>
          </ul>
          <p>When the cursor is locked, you can look around freely. When it's unlocked (by pressing the middle mouse button or opening a menu), you can interact with UI elements like buttons and menus.</p>
        </>
      )
    },
    dailyPool: {
      title: "Daily $REDDICKS Pool",
      content: (
        <>
          <p>The Daily Pool represents the total amount of $REDDICKS tokens rewarded to all players combined, each day.</p>
          <p>Currently, the pool distributes <strong>{dailyTokenPool.toLocaleString()} $REDDICKS</strong> daily.</p>
          <p>This distribution model is designed to sustain the Play-to-Earn rewards until at least 2028, ensuring long-term value and engagement for our players.</p>
          <p>Your share of this pool depends on your Mining Power relative to the total power of all active players.</p>
        </>
      )
    },
    miningPower: {
      title: "Mining Power",
      content: (
        <>
          <p>Mining Power determines your share of the Daily $REDDICKS Pool. The more Mining Power you have, the more tokens you earn per hour.</p>
          <p>Each Mining Rig tier provides a specific amount of Power:</p>
          <ul>
            {/* You might need to pass rigTierPowers via gameData if not already there */} 
            <li>Tier 1: {getConstant(gameData, 'RIG_TIER_POWERS', [10])[0]} Power</li>
            <li>Tier 2: {getConstant(gameData, 'RIG_TIER_POWERS', [10, 15])[1]} Power</li>
            <li>Tier 3: {getConstant(gameData, 'RIG_TIER_POWERS', [10, 15, 25])[2]} Power</li>
            <li>Tier 4: {getConstant(gameData, 'RIG_TIER_POWERS', [10, 15, 25, 40])[3]} Power</li>
          </ul>
          <p>Your hourly reward is calculated based on your power's proportion of the total network power (currently simulated), distributed from the daily pool, but capped at a maximum of <strong>{maxHourlyReward} $REDDICKS/hr</strong>.</p>
          {/* Example Calculation Visualization - adjust numbers as needed */}
          {/* 
          <p><strong>Example:</strong> If your total power is 50 and the total network power is 1000:</p>
          <p>Your Share = (50 / 1000) = 5%</p>
          <p>Your Daily Earnings = 5% of {dailyTokenPool.toLocaleString()} = {(0.05 * dailyTokenPool).toLocaleString()} $REDDICKS</p>
          <p>Your Uncapped Hourly Rate = {(0.05 * dailyTokenPool / 24).toFixed(2)} $REDDICKS/hr</p>
          <p>Your Actual Hourly Rate = Minimum({(0.05 * dailyTokenPool / 24).toFixed(2)}, {maxHourlyReward}) = {Math.min((0.05 * dailyTokenPool / 24), maxHourlyReward).toFixed(2)} $REDDICKS/hr</p>
          */}
        </>
      )
    },
    energy: {
      title: "Energy",
      content: (
        <>
          <p>Energy is required for your Mining Rigs to operate and generate $REDDICKS.</p>
          <p>Currently, the energy system is under development, and all rigs have <strong>unlimited energy</strong>.</p>
          <p>In the future, we plan to introduce exciting gameplay mechanics where you'll need to actively manage or earn energy to keep your mining operation running at peak efficiency. Stay tuned for updates!</p>
        </>
      )
    }
  };

  return (
    <div className="board-menu-overlay" onClick={onClose}> {/* Close on overlay click */}
      <div className="board-container">
        <button className="close-button" onClick={onClose} aria-label="Close">×</button>
        <div className="board-menu" onClick={(e) => e.stopPropagation()}> {/* Prevent closing when clicking inside menu */}
          <div className="board-layout">
            <nav className="board-nav">
              <ul>
                {Object.entries(sections).map(([key, { title }]) => (
                  <li key={key} className={activeSection === key ? 'active' : ''}>
                    <button onClick={() => setActiveSection(key)}>{title}</button>
                  </li>
                ))}
              </ul>
            </nav>
            <section className="board-content">
              <h2>{sections[activeSection].title}</h2>
              <div className="content-area">
                {sections[activeSection].content}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Board;
